import React from "react";
import classNames from "classnames";
import { useMediaQuery } from "@shared/hooks/useMediaQuery";
import "./OverflowMarquee.scss";

/** Пропсы компонента {@link OverflowMarquee}. */
export interface OverflowMarqueeProps {
    /** Текст или inline-контент, который нужно прокручивать при горизонтальном overflow. */
    children: React.ReactNode;
    /**
     * Выключает измерение и авто-прокрутку.
     * @default false
     */
    disabled?: boolean;
    /** Дополнительный CSS-класс контейнера, который ограничивает видимую ширину. */
    trackClassName?: string;
    /** Дополнительный CSS-класс прокручиваемого контента. */
    contentClassName?: string;
}

type OverflowMarqueeStyle = React.CSSProperties & {
    "--overflow-marquee-distance"?: string;
    "--overflow-marquee-duration"?: string;
};

const TEXT_METRIC_PROPS = [
    "font",
    "fontSize",
    "fontFamily",
    "fontWeight",
    "fontStyle",
    "letterSpacing",
    "wordSpacing",
    "textTransform",
] as const;

/** Комфортная скорость чтения для мелкого UI-текста при авто-прокрутке, px/s. */
const SCROLL_SPEED_PX_PER_S = 32;

/** Доля полного ping-pong-цикла на один проход (14% -> 36% и 64% -> 86%). */
const SCROLL_LEG_CYCLE_FRACTION = 0.22;

/** Минимальная и максимальная длительность полного ping-pong-цикла, мс. */
const SCROLL_MIN_CYCLE_MS = 10_000;
const SCROLL_MAX_CYCLE_MS = 36_000;

function measureTextNodeWidth(content: HTMLElement): number {
    const range = document.createRange();
    range.selectNodeContents(content);
    return range.getBoundingClientRect().width;
}

function measureContentWidth(content: HTMLElement): number {
    const clone = content.cloneNode(true);
    if (!(clone instanceof HTMLElement)) {
        return Math.max(content.scrollWidth, content.offsetWidth, content.getBoundingClientRect().width);
    }

    const computed = window.getComputedStyle(content);
    clone.style.position = "absolute";
    clone.style.visibility = "hidden";
    clone.style.pointerEvents = "none";
    clone.style.left = "-9999px";
    clone.style.top = "0";
    clone.style.display = "inline-block";
    clone.style.width = "auto";
    clone.style.maxWidth = "none";
    clone.style.whiteSpace = "nowrap";
    clone.style.animation = "none";
    clone.style.transform = "none";

    for (const prop of TEXT_METRIC_PROPS) {
        clone.style[prop] = computed[prop];
    }

    document.body.appendChild(clone);
    const width = clone.getBoundingClientRect().width;
    document.body.removeChild(clone);

    return width;
}

function measureScrollDistance(track: HTMLElement, content: HTMLElement): number {
    const trackWidth = track.clientWidth;
    if (trackWidth <= 0) {
        return 0;
    }

    const contentWidth = Math.max(
        measureContentWidth(content),
        track.scrollWidth,
        Math.max(content.scrollWidth, content.offsetWidth, content.getBoundingClientRect().width),
        measureTextNodeWidth(content),
    );

    return Math.ceil(contentWidth - trackWidth);
}

function getScrollDurationMs(scrollDistance: number): number {
    if (scrollDistance <= 0) {
        return SCROLL_MIN_CYCLE_MS;
    }

    const scrollLegMs = (scrollDistance / SCROLL_SPEED_PX_PER_S) * 1000;
    const cycleMs = scrollLegMs / SCROLL_LEG_CYCLE_FRACTION;

    return Math.min(Math.max(Math.round(cycleMs), SCROLL_MIN_CYCLE_MS), SCROLL_MAX_CYCLE_MS);
}

/**
 * Inline-контент с ping-pong авто-прокруткой только при горизонтальном overflow. \
 * Учитывает `prefers-reduced-motion` и в этом режиме оставляет обычный ellipsis.
 */
export function OverflowMarquee({ children, disabled = false, trackClassName, contentClassName }: OverflowMarqueeProps) {
    const trackRef = React.useRef<HTMLDivElement | null>(null);
    const contentRef = React.useRef<HTMLSpanElement | null>(null);
    const [scrollDistance, setScrollDistance] = React.useState(0);
    const [shouldAnimate, setShouldAnimate] = React.useState(false);
    const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

    React.useLayoutEffect(() => {
        if (disabled) {
            setScrollDistance(0);
            setShouldAnimate(false);
            return;
        }

        const track = trackRef.current;
        const content = contentRef.current;
        if (track == null || content == null) {
            return;
        }

        const measure = (): void => {
            const currentTrack = trackRef.current;
            const currentContent = contentRef.current;
            if (currentTrack == null || currentContent == null) {
                return;
            }

            const overflow = measureScrollDistance(currentTrack, currentContent);

            if (overflow > 1) {
                setScrollDistance((currentDistance) => (currentDistance === overflow ? currentDistance : overflow));
                setShouldAnimate(!prefersReducedMotion);
                return;
            }

            setScrollDistance((currentDistance) => (currentDistance === 0 ? currentDistance : 0));
            setShouldAnimate(false);
        };

        measure();

        let cancelled = false;
        const frameId = window.requestAnimationFrame(measure);
        void document.fonts?.ready.then(() => {
            if (!cancelled) {
                measure();
            }
        });

        const observer = new ResizeObserver(measure);
        observer.observe(track);

        return () => {
            cancelled = true;
            window.cancelAnimationFrame(frameId);
            observer.disconnect();
        };
    }, [children, disabled, prefersReducedMotion]);

    React.useLayoutEffect(() => {
        const content = contentRef.current;
        if (content == null || !shouldAnimate || scrollDistance <= 0) {
            return;
        }

        content.style.animation = "none";
        void content.offsetWidth;
        content.style.removeProperty("animation");
    }, [scrollDistance, shouldAnimate]);

    const scrollStyle: OverflowMarqueeStyle | undefined =
        shouldAnimate && scrollDistance > 0
            ? {
                  "--overflow-marquee-distance": `${scrollDistance}px`,
                  "--overflow-marquee-duration": `${getScrollDurationMs(scrollDistance)}ms`,
              }
            : undefined;

    return (
        <div ref={trackRef} className={classNames("overflow_marquee", trackClassName)}>
            <span
                ref={contentRef}
                className={classNames(
                    "overflow_marquee__content",
                    shouldAnimate && "overflow_marquee__content--scroll",
                    contentClassName,
                )}
                style={scrollStyle}
            >
                {children}
            </span>
        </div>
    );
}
